import { useApp } from 'contexts/app';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import useElapsedTime from 'services/hooks/useElapsedTime';
import submissionService from 'services/submission';
import { getCriterionMaxPoints, getRubricMaxPoints, getTemplatedFeedback, isValidRate, stripHTML } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { currentAssignment, submissions, setSubmissions, studentAccountUsers } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { submissionId } = useParams();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [selectedFileId, setSelectedFileId] = useState('');
    const [tab, setTab] = useState('grades');
    const [selectedCriterionId, setSelectedCriterionId] = useState(null);
    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [selectedInlineCommentId, setSelectedInlineCommentId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState(null);
    const [gradeOverrides, setGradeOverrides] = useState({});
    const [meta, setMeta] = useState({
        sessionStart: 0,
        excludedTime: 0,
    });
    const [showDetails, setShowDetails] = useState(false);
    const [isSuggestionVisible, setIsSuggestionVisible] = useState(false);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);

    const { createSession, addSessionToSubmission } = useElapsedTime({ meta, setMeta });

    const viewableSubmissions = submissions
        ?.filter(({ grading }) => grading)
        .sort((a, b) => {
            const aName = a.files[0].name.toLowerCase();
            const bName = b.files[0].name.toLowerCase();
            if (aName < bName) return -1;
            if (aName > bName) return 1;
            return 0;
        });
    const currentSubmission = viewableSubmissions?.find(({ id }) => id === submissionId);

    const selectedCriterionTitle = currentAssignment?.rubric?.criteria?.find(
        ({ id }) => id === selectedCriterionId
    )?.title;

    const getHighlights = useCallback(() => {
        const format = (items) => {
            return (items ?? []).map((item) => {
                return {
                    id: selectedInlineCommentId ?? selectedCriterionId ?? selectedFeedbackId,
                    value: item,
                };
            });
        };

        if (tab === 'grades' && selectedCriterionId) {
            const locations = currentSubmission?.ai?.grading?.[selectedCriterionId]?.locations;
            return format(locations);
        }

        if (tab === 'feedback' && selectedFeedbackId) {
            const feedback = currentSubmission?.ai?.feedback;
            const isRubric = feedback?.feedback_type === 'rubric';
            const suggestion = form.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId);
            const locations = isRubric ? suggestion?.negative?.locations : suggestion?.locations;
            return format(locations);
        }

        const comments = form?.inline_comments ?? [];
        const activeHighlights = comments
            .filter(({ active }) => active)
            .filter(({ id }) => id === selectedInlineCommentId)
            .map(({ highlight }) => highlight);

        return format(activeHighlights);
    }, [
        currentSubmission?.ai?.feedback,
        currentSubmission?.ai?.grading,
        form?.feedback?.improvement_suggestions,
        form?.inline_comments,
        selectedCriterionId,
        selectedFeedbackId,
        selectedInlineCommentId,
        tab,
    ]);

    const isFormValid = useMemo(() => {
        if (!currentAssignment || !form) return false;
        const isGradingValid = currentAssignment.rubric.criteria.every(
            ({ id }) => Boolean(form.criteria[id]) || Boolean(gradeOverrides[id])
        );
        const isFeedbackValid = Boolean(form.feedback);

        if (currentAssignment?.ai_feedback_enabled) {
            return isGradingValid && isFeedbackValid;
        } else if (currentAssignment?.ai_grading_enabled) {
            return isGradingValid;
        }
    }, [currentAssignment, form, gradeOverrides]);

    const areOverridesValid = useMemo(() => {
        if (!Object.entries(gradeOverrides).length) return true;
        return Object.entries(gradeOverrides)
            .map(([criterionId, overrideValue]) => {
                if (criterionId === 'total') {
                    return (
                        isValidRate(overrideValue) &&
                        Number(overrideValue) >= 0 &&
                        Number(overrideValue) <= getRubricMaxPoints(currentAssignment.rubric.criteria)
                    );
                } else {
                    return (
                        isValidRate(overrideValue) &&
                        Number(overrideValue) >= 0 &&
                        Number(overrideValue) <=
                            getCriterionMaxPoints(
                                currentAssignment.rubric.criteria.find(({ id }) => id === criterionId)
                            )
                    );
                }
            })
            .reduce((acc, cur) => acc && cur, true);
    }, [currentAssignment?.rubric?.criteria, gradeOverrides]);

    const isCurrentFormValid = useMemo(() => isFormValid && areOverridesValid, [isFormValid, areOverridesValid]);

    const onClickBack = () => {
        history.push(`/assignments/${currentAssignment.id}/submissions`);
    };

    const onChangeSubmission = (_, { props: { value: submissionId } }) => {
        history.push(`/assignments/${currentAssignment.id}/submissions/${submissionId}`);
    };

    const viewPrevSubmission = () => {
        const index = viewableSubmissions.findIndex(({ id }) => id === currentSubmission.id);
        const prevSubmission = viewableSubmissions[index - 1];
        history.push(`/assignments/${currentAssignment.id}/submissions/${prevSubmission.id}`);
    };

    const viewNextSubmission = () => {
        const index = viewableSubmissions.findIndex(({ id }) => id === currentSubmission.id);
        const nextSubmission = viewableSubmissions[index + 1];
        history.push(`/assignments/${currentAssignment.id}/submissions/${nextSubmission.id}`);
    };

    const onClickEdit = () => {
        setForm({ ...currentSubmission.grading, grading_explanations: currentSubmission.grading_explanations ?? {} });
        setGradeOverrides(currentSubmission.grade_overrides ?? {});
        setMeta({
            sessionStart: new Date().getTime(),
            excludedTime: 0,
        });
        setIsEditing(true);
    };

    const onClickEditCancel = () => {
        setDialog({
            open: true,
            message: t('common:dialog.discard-changes.title', 'Discard Changes?'),
            description: t(
                'common:dialog.discard-changes.description',
                'If you continue, your changes will be lost. To save them, please click “Save.”'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.discard', 'Discard'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setForm({
                    ...currentSubmission.grading,
                    grading_explanations: currentSubmission.grading_explanations ?? {},
                });
                setGradeOverrides(currentSubmission.grade_overrides ?? {});
                setMeta({
                    sessionStart: 0,
                    excludedTime: 0,
                });
                setIsEditing(false);
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickEditSave = () => {
        const updatedForm =
            currentSubmission.ai?.feedback?.feedback_type === 'templated'
                ? {
                      ...form,
                      feedback: {
                          ...form.feedback,
                          commentary: getTemplatedFeedback(currentAssignment, form),
                      },
                  }
                : form;
        const updatedSubmission = addSessionToSubmission(createSession('confirm'), {
            ...omit(currentSubmission, ['_id']),
            modified_on: new Date().getTime(),
            grading: omit(updatedForm, ['grading_explanations']),
            grade_overrides: gradeOverrides,
            ...(Object.keys(form.grading_explanations).length && {
                grading_explanations: form.grading_explanations,
            }),
        });
        setSubmissions((current) => {
            return current.map((submission) => {
                return submission.id === updatedSubmission.id ? updatedSubmission : submission;
            });
        });
        submissionService.updateSubmission(updatedSubmission.id, updatedSubmission);
        setIsEditing(false);
    };

    const onSelect = async ({ key }) => {
        if (key === 'copy-all') {
            const noFeedbackTranslationArgs = currentAssignment?.api_assignment_id
                ? [
                      'feedback-section.no-feedback-no-pushback',
                      'No feedback provided. This message will not be passed back.',
                  ]
                : ['feedback-section.no-feedback', 'No feedback provided.'];

            let feedback = '';
            if (typeof currentSubmission.grading?.feedback === 'string') {
                feedback += stripHTML(form.feedback);
            } else {
                feedback += stripHTML(form.feedback.commentary);
                if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                    form.feedback.improvement_suggestions.forEach(({ criterion_id, positive, negative }, index) => {
                        if (index || form.feedback.commentary) {
                            feedback += '\n\n';
                        }
                        const criterion = currentAssignment.rubric.criteria.find(({ id }) => id === criterion_id);
                        feedback += `${criterion.title}\n\n`;
                        feedback += `${t('feedback-section.positive-title', 'What you did well')}\n\n`;
                        feedback +=
                            !positive.active && positive.text
                                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                : positive.text === ''
                                  ? t(...noFeedbackTranslationArgs)
                                  : stripHTML(positive.text);
                        feedback += `\n\n${t('feedback-section.improvement-title', 'How you can improve')}\n\n`;
                        feedback +=
                            !negative.active && negative.text
                                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                : negative.text === ''
                                  ? t(...noFeedbackTranslationArgs)
                                  : stripHTML(negative.text);
                    });
                } else if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                    form.feedback.improvement_suggestions
                        .filter(({ active }) => active)
                        .forEach(({ text }, index) => {
                            if (index || form.feedback.commentary) {
                                feedback += '\n\n';
                            }
                            feedback += stripHTML(text);
                        });
                }
            }
            await navigator.clipboard.writeText(feedback);
            message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: t('dialog.delete-all-feedback.title', 'Delete All Feedback?'),
                description: t(
                    'dialog.delete-all-feedback.description',
                    'Are you sure you want to delete all feedback? This can’t be undone.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => {
                        const updatedForm = {
                            ...current,
                            feedback: {
                                commentary: '',
                                improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                                    if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                                        return {
                                            ...item,
                                            positive: {
                                                ...item.positive,
                                                active: false,
                                            },
                                            negative: {
                                                ...item.negative,
                                                active: false,
                                            },
                                        };
                                    }
                                    if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                                        return {
                                            ...item,
                                            active: false,
                                        };
                                    }
                                    return item;
                                }),
                            },
                        };
                        const updatedSubmission = {
                            ...currentSubmission,
                            modified_on: new Date().getTime(),
                            grading: omit(updatedForm, ['grading_explanations']),
                        };
                        setSubmissions((submissions) => {
                            return submissions.map((submission) => {
                                if (submission.id !== updatedSubmission.id) return submission;
                                return updatedSubmission;
                            });
                        });
                        submissionService.updateSubmission(updatedSubmission.id, omit(updatedSubmission, ['_id']));
                        return updatedForm;
                    });
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const updateSubmission = (updatedForm) => {
        const updatedSubmission = {
            ...currentSubmission,
            modified_on: new Date().getTime(),
            grading: omit(updatedForm, ['grading_explanations']),
        };
        setSubmissions((submissions) => {
            return submissions.map((submission) => {
                if (submission.id !== updatedSubmission.id) return submission;
                submissionService.updateSubmission(updatedSubmission.id, omit(updatedSubmission, ['_id']));
                return updatedSubmission;
            });
        });
    };

    const handleCriterionNavigation = (criterionId) => {
        setSelectedCriterionId(criterionId);
        setTab(tab === 'grades' ? 'feedback' : 'grades');
        const matchedSuggestion = form?.feedback?.improvement_suggestions?.find((s) => s.criterion_id === criterionId);
        if (matchedSuggestion) {
            setSelectedFeedbackId(matchedSuggestion.id);
        }
    };

    useEffect(() => {
        if (!currentSubmission) return;
        setSelectedFileId(currentSubmission.files[0].id);
        setForm({ ...currentSubmission.grading, grading_explanations: currentSubmission.grading_explanations ?? {} });
        setGradeOverrides(currentSubmission.grade_overrides ?? {});
        setMeta({ sessionStart: 0, excludedTime: 0 });
        setSelectedCriterionId(currentAssignment.rubric.criteria[0].id);
        if (['rubric', 'system'].includes(currentSubmission.grading.feedback?.feedback_type)) {
            const activeSuggestions = currentSubmission.grading.feedback.improvement_suggestions.filter(
                ({ active }) => active
            );
            if (activeSuggestions[0]) {
                setSelectedFeedbackId(activeSuggestions[0].id);
            }
        }
    }, [currentAssignment, currentSubmission]);

    useEffect(() => {
        if (selectedCriterionId) {
            setSelectedInlineCommentId(null);
        }
    }, [selectedCriterionId]);

    useEffect(() => {
        if (selectedFeedbackId) {
            setSelectedInlineCommentId(null);
        }
    }, [selectedFeedbackId]);

    useEffect(() => {
        if (selectedInlineCommentId !== null) {
            if (tab === 'grades') {
                setSelectedCriterionId(null);
            }
            if (tab === 'feedback') {
                setSelectedFeedbackId(null);
            }
        }
    }, [selectedInlineCommentId, tab]);

    return (
        <PageContext.Provider
            value={{
                currentAssignment,
                viewableSubmissions,
                currentSubmission,
                studentAccountUsers,
                selectedCriterionTitle,
                highlights: getHighlights(),
                selectedFileId,
                setSelectedFileId,
                tab,
                setTab,
                selectedCriterionId,
                setSelectedCriterionId,
                selectedFeedbackId,
                setSelectedFeedbackId,
                selectedInlineCommentId,
                setSelectedInlineCommentId,
                isEditing,
                form,
                setForm,
                isCurrentFormValid,
                gradeOverrides,
                setGradeOverrides,
                showDetails,
                setShowDetails,
                isSuggestionVisible,
                setIsSuggestionVisible,
                isDocumentFullScreen,
                setIsDocumentFullScreen,
                onClickBack,
                onChangeSubmission,
                viewPrevSubmission,
                viewNextSubmission,
                onClickEdit,
                onClickEditCancel,
                onClickEditSave,
                onSelect,
                updateSubmission,
                handleCriterionNavigation,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
